import type { Product } from '@/domain/Product';
import type { CollectionCard, CollectionProductCard } from './cards';

/**
 * Returns all products from a given grid row, depending on grid size.
 * Only returns products for the first Product in the row.
 * Columns count dependent on viewport size & presence of Promo Cards.
 * @param productWithImpression - The product currently emitting an impression
 * @param cards - All promo/product cards assigned to the current Grid
 * @param gridColumns - Current value of --grid-columns for Grid
 */
export default function mapRowProducts(
  productWithImpression: Product,
  cards: CollectionCard[],
  gridColumns = 4
): Product[] {
  // If only 1 column (or potentially zero) per grid-row (small mobile) - always track with current product
  if (gridColumns < 2) {
    return [productWithImpression];
  }

  // Map product ids to include any before/after-product promo cards
  // e.g. [id1, id2, promo, id3, promo, id4, id5, promo, etc..]
  const gridIds: string[] = cards.map((card) =>
    card.type === 'promotion' ? 'promo' : card.product.id
  );

  const gridRows: string[][] = [];

  for (let i = 0; i < gridIds.length; i += gridColumns) {
    gridRows.push(gridIds.slice(i, i + gridColumns));
  }

  // Get the grid-row for the product currently emitting an impression
  const currentRow = gridRows.find((row) =>
    row.includes(productWithImpression.id)
  );

  if (!currentRow) return [];

  // Get the first instance of a product in the current row (excluding promos)
  const firstProductInCurrentRow = currentRow.find((card) => card !== 'promo');

  // if the product currently emitting an impression is also the first element in the current row, return all products from that row
  if (firstProductInCurrentRow === productWithImpression.id) {
    return cards
      .filter(
        (card) =>
          card.type === 'product' && currentRow.includes(card.product.id)
      )
      .map((card) => (card as CollectionProductCard).product);
  }

  return [];
}
